<template>
  <div>
    <table class="table table-header">
      <tbody>
        <tr>
          <th scope="row" width="15%">御利用月：</th>
          <td width="35%">
            {{ filteredMonthDisplay }}
            <b-icon
              icon="question-circle"
              v-b-tooltip.hover.v-light
              title="システムにより自動的に生成されています。記載されている金額は、実際の請求と異なる場合もあります。"
              v-if="filteredMonthDisplay.length > 1"
            />
          </td>
          <td rowspan="5" v-if="showRecipientDetails">
            <img src="@/assets/images/Sign Stamp.png" class="float-right" />
            <p class="recipient-title">株式会社タマイインベストメントエデュケーションズ</p>
            <p class="recipient-detail">〒604-8153</p>
            <p class="recipient-detail">京都府京都市中京区烏丸通四条上る笋町688 第15長谷ビル5F</p>
            <p class="recipient-detail">Tel 075-708-8100 Fax 075-708-8101 tie@tamaishiki.com</p>
            <img src="@/assets/images/Sign Placeholder.png" />
          </td>
        </tr>
        <tr>
          <th scope="row">御法人名：</th>
          <td>{{ selectedCorporationNameDisplay }}</td>
        </tr>
        <tr v-if="selectedBrand">
          <th scope="row"></th>
          <td>{{ selectedBrandNameDisplay }}</td>
        </tr>
        <tr v-if="selectedSchool">
          <th scope="row"></th>
          <td>{{ selectedSchoolNameDisplay }}</td>
        </tr>
        <tr>
          <th scope="row"><b>御請求金額：</b></th>
          <td>
            <b>{{ formattedMoneyAmountWithCurrencyDisplay(totalBillingAfterVATAmount) }}</b>
          </td>
        </tr>
      </tbody>
    </table>

    <b-table
      ref="billing-table"
      class="billing-table"
      :min-width="tableWidth"
      responsive
      :items="billings"
      :fields="fields"
      :per-page="totalBillings"
      :current-page="currentPage"
      :busy="currentBusyState"
    >
      <template v-slot:head()="scope">
        <div class="text-nowrap custom-header">
          {{ scope.label }}
        </div>
      </template>

      <template v-slot:head(quantity)="scope">
        <div class="text-nowrap custom-header unit-align">{{ scope.label }}</div>
      </template>

      <template v-slot:head(unit)="scope">
        <div class="text-nowrap custom-header unit-align">{{ scope.label }}</div>
      </template>

      <template v-slot:head(price)="scope">
        <div class="text-nowrap custom-header unit-align">{{ scope.label }}</div>
      </template>

      <template v-slot:head(totalPrice)="scope">
        <div class="text-nowrap custom-header unit-align">{{ scope.label }}</div>
      </template>

      <template v-slot:cell()="row">
        <div class="text-nowrap custom-row" :class="{ 'striped-cell': row.index % 2 == 0 }">
          {{ row.value }}
        </div>
      </template>

      <template v-slot:cell(billingType)="row">
        <div class="text-nowrap custom-row" :class="{ 'striped-cell': row.index % 2 == 0 }">
          <span v-if="shouldShowRow(row)">{{ billingTypeDisplay(row.value) }}</span>
          <span v-else></span>
        </div>
      </template>

      <template v-slot:cell(billingName)="row">
        <div class="text-nowrap custom-row" :class="{ 'striped-cell': row.index % 2 == 0 }">
          <div :class="{ 'long-text-box': row.value.length > 14 }">
            <span v-if="shouldShowRow(row)">
              <a class="hyper-text" @click="showBillingIdDetail(row.item)" v-if="row.item.canRequestMoreDetail">
                {{ row.value }}
              </a>
              <span v-else>
                {{ row.value }}
              </span>
            </span>
            <span v-else></span>
          </div>
        </div>
      </template>

      <template v-slot:cell(billingTypeId)="row">
        <div class="text-nowrap custom-row" :class="{ 'striped-cell': row.index % 2 == 0 }">
          <div :class="{ 'long-text-box': billingTypeIdDisplay(row.value).length > 14 }">
            {{ billingTypeIdDisplay(row.value) }}
            <span class="new-billable" v-if="row.item.hasNewBillable">NEW!</span>
            <b-icon
              icon="question-circle"
              v-b-tooltip.hover.v-light
              :title="billingTypeIdTipsDisplay(row.value)"
              v-if="showBillingTypeIdTips(row.value)"
            />
          </div>
        </div>
      </template>

      <template v-slot:cell(quantity)="row">
        <div class="text-nowrap custom-row price-align" :class="{ 'striped-cell': row.index % 2 == 0 }">
          <a class="hyper-text" @click="showBillingQuantityDetail(row.item.no)" v-if="shouldShowItemTypeDetail(row)">
            {{ formattedMoneyAmountDisplay(row.value) }}
          </a>
          <span v-else>
            {{ formattedMoneyAmountDisplay(row.value) }}
          </span>
        </div>
      </template>

      <template v-slot:cell(unit)="row">
        <div class="text-nowrap custom-row unit-align" :class="{ 'striped-cell': row.index % 2 == 0 }">
          {{ row.value }}
        </div>
      </template>

      <template v-slot:cell(price)="row">
        <div class="text-nowrap custom-row price-align" :class="{ 'striped-cell': row.index % 2 == 0 }">
          {{ formattedMoneyAmountDisplay(row.value) }}
        </div>
      </template>

      <template v-slot:cell(totalPrice)="row">
        <div class="text-nowrap custom-row price-align" :class="{ 'striped-cell': row.index % 2 == 0 }">
          {{ formattedMoneyAmountDisplay(row.value) }}
        </div>
      </template>

      <template v-slot:table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>{{ currentBusyDisplay }}</strong>
        </div>
      </template>
    </b-table>

    <div class="total-table" v-show="showResultTable">
      <table class="table">
        <tbody>
          <tr>
            <th scope="row" />
            <td width="20%" class="striped-cell">小計（税抜）</td>
            <td width="14%" class="striped-cell total-price-align">
              {{ formattedMoneyAmountDisplay(totalBillingAmount) }}
            </td>
          </tr>
          <tr>
            <th scope="row" />
            <td>消費税10%</td>
            <td class="total-price-align">
              {{ formattedMoneyAmountDisplay(totalVATAmount) }}
            </td>
          </tr>
          <tr>
            <th scope="row" />
            <td class="striped-cell"><b>合計金額</b></td>
            <td class="striped-cell total-price-align">
              <b>{{ formattedMoneyAmountDisplay(totalBillingAfterVATAmount) }}</b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="custom-explanation-content" v-if="showResultTable">
      <div class="custom-explanation-header">備考</div>
      <div class="custom-explanation-body">
        ※御利用月に（自動集計）と記載がある場合、記載されている金額は実際の請求と異なる場合があります。
      </div>
      <div class="custom-explanation-body">
        ※休会中の生徒は、生徒IDにはカウントされません。
      </div>
      <div class="custom-explanation-body">
        ※月の途中で教場やブランドの紐づけを変更した場合、変更後の教場やブランドとしてカウントされます。
      </div>
      <br />
      <br />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  basicFieldPresent,
  basicMoneyAmount,
  basicBillingTypeDisplay,
  basicBillingTypeIdDisplay,
} from '@/presenters/fields';
import { sumObjectArrayByKey } from '@/utils/math_helpers.js';

export default {
  props: {
    filteredMonthDisplay: {
      type: String,
      default: '',
    },
    selectedCorporation: {
      type: Object,
      default: null,
    },
    selectedBrand: {
      type: Object,
      default: null,
    },
    selectedSchool: {
      type: Object,
      default: null,
    },
    shouldDisplayTotalFee: {
      type: Boolean,
      defauft: true,
    },
    formattedMoneyAmountWithCurrency: {
      type: String,
      default: '',
    },
    showRecipientDetails: {
      type: Boolean,
      default: false,
    },
    currentBusyState: {
      type: Boolean,
      default: false,
    },
    currentBusyDisplay: {
      type: String,
      default: 'データのロード...',
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'no',
          label: 'No.',
          thAttr: { width: 20 },
        },
        {
          key: 'billingType',
          label: '区分',
          thAttr: { width: 50 },
        },
        {
          key: 'billingName',
          label: 'ID名',
          thAttr: { width: 150 },
        },
        {
          key: 'billingTypeId',
          label: '項目',
          thAttr: { width: 255 },
        },
        {
          key: 'quantity',
          label: '数量',
          thAttr: { width: 20 },
        },
        {
          key: 'unit',
          label: '単位',
          thAttr: { width: 50 },
        },
        {
          key: 'price',
          label: '単価',
          thAttr: { width: 80 },
        },
        {
          key: 'totalPrice',
          label: '合計',
          thAttr: { width: 100 },
          tdStyle: { textAlign: 'right' },
        },
      ],
      currentPage: 1,
    };
  },
  computed: {
    tableWidth() {
      return sumObjectArrayByKey(this.filteredHeaders, 'thAttr', 'width') + 'px';
    },
    selectedCorporationNameDisplay() {
      return basicFieldPresent(this.selectedCorporation, 'name');
    },
    selectedBrandNameDisplay() {
      return basicFieldPresent(this.selectedBrand, 'name');
    },
    selectedSchoolNameDisplay() {
      return basicFieldPresent(this.selectedSchool, 'name');
    },
    showResultTable() {
      return this.totalBillings > 0 && !this.currentBusyState;
    },
    ...mapState({
      billings: (state) => state.billing.billings,
    }),
    ...mapGetters('billing', [
      'getBillingsByBillingId',
      'getBillingByNo',
      'totalBillings',
      'totalBillingAmount',
      'totalVATAmount',
      'totalBillingAfterVATAmount',
    ]),
  },
  methods: {
    formattedMoneyAmountWithCurrencyDisplay(amount) {
      if (this.shouldDisplayTotalFee) {
        return basicMoneyAmount(amount, true);
      }
      return '―';
    },
    formattedMoneyAmountDisplay(amount) {
      return basicMoneyAmount(amount, false);
    },
    shouldShowRow(row) {
      if (row.index == 0) {
        return true;
      } else if (row.item.billingId == this.billings[row.index - 1].billingId) {
        return false;
      }
      return true;
    },
    shouldShowItemTypeDetail(row) {
      if (
        [
          'studentKiwami1',
          'studentKiwami2',
          'studentKokugo',
          'studentAsobi',
          'studentSatori',
          'student1',
          'student2',
          'teacher',
        ].includes(row.item.billingTypeId)
      ) {
        return true;
      }
      return false;
    },
    billingTypeDisplay(type) {
      return basicBillingTypeDisplay(type);
    },
    billingTypeIdDisplay(type) {
      return basicBillingTypeIdDisplay(type);
    },
    billingTypeIdTipsDisplay(type) {
      switch (type) {
        case 'teacherFreeTrial':
          return '先生IDを登録した月は、無料で先生IDを利用できます。生徒やクラスの設定にご利用ください。';
        case 'videoViewing':
          return '教場IDの数と同じ数の映像視聴IDをご利用していただくことを想定しています。映像視聴用IDを発行していなくても請求対象となりますのでご注意ください。';
        case 'additionalVideoViewing':
          return 'いずれかの教場で2つ以上の映像視聴IDを発行した場合にこの項目にカウントされます。';
        default:
          return '';
      }
    },
    showBillingTypeIdTips(type) {
      switch (type) {
        case 'teacherFreeTrial':
        case 'videoViewing':
        case 'additionalVideoViewing':
          return true;
        default:
          return false;
      }
    },
    showBillingIdDetail(billingItem) {
      this.$logger.debug(
        'Show billing detail for billingId ' + billingItem.billingId + ' type ' + billingItem.billingType
      );
      const payload = {
        billingId: billingItem.billingId,
        billingType: billingItem.billingType,
      };
      this.$emit('goToBillingPage', payload);
    },
    showBillingQuantityDetail(no) {
      this.$logger.debug('Show billing quantity detail for no ' + no);
      let currentBilling = this.getBillingByNo(no);
      this.selectBillingDetails(currentBilling.items);
      this.$emit('showDetailsPopupHolder', currentBilling);
    },
    ...mapActions('billing', ['selectBillingDetails']),
  },
};
</script>

<style scoped>
.custom-header-holder {
  background-color: #7e7964;
}
.custom-header {
  background-color: #7e7964;
  color: white;
  padding: 5px 10px 5px 10px;
}
.custom-row {
  height: 42px;
  padding: 0 10px;
  display: flex;
  align-items: center;
}
.billing-table {
  margin-bottom: 0px;
}
.striped-cell {
  background: #eeeeee;
}
.hyper-text {
  color: #227afa;
  cursor: pointer;
}
.unit-align {
  text-align: center;
}
.price-align {
  text-align: right;
  justify-content: end;
  padding-right: 10px;
}
.total-table {
  padding: 0px;
}
.total-price-align {
  text-align: right;
  margin-right: 10px;
}
.recipient-title {
  font-size: 12px;
  font-weight: bold;
}
.recipient-detail {
  font-size: 11px;
}
.custom-explanation-header {
  background-color: #7e7964;
  color: white;
  padding: 5px 10px 5px 10px;
}
.custom-explanation-content {
  border: 1px solid #7e7964;
}
.custom-explanation-body {
  padding: 10px 20px 5px 20px;
}
.long-text-box {
  font-size: 12px;
  white-space: normal;
}
.new-billable {
  font-size: 8px;
}
</style>
